import React, { useEffect, useState } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'
import { useProject } from 'hooks/useProject'
import InformationBox from 'views/settings/components/information'
import Formatting from 'views/settings/components/formatting'
import PermissionsBox from 'views/settings/components/permissions'
import Notifications from 'views/settings/components/notifications'
import Secrets from 'views/settings/components/secrets'
import ErrorBoundary from 'components/error'
import Audit from 'components/audit'
import BulkUpdates from 'views/settings/components/bulkUpdates'
import DeletedResources from 'views/settings/components/deleted'
import ScriptsPanel from 'views/settings/components/scripts'
import SectionNav from 'views/settings/components/sectionNav'
import PageHeader from 'components/pageHeader'
import api from 'helpers/api'
import { capitaliseFirstLetter, getQueryParameter } from 'helpers/utils'
import { HUB_SETTINGS } from 'routes'

/**
 * The section a `?tab=` names. Archived was called Deleted, and Members took in Invites, so links to
 * `?tab=deleted` and `?tab=invites` still open them.
 */
const sectionFor = (tab: string) => {
  if (tab === 'deleted') return 'Archived'
  if (tab === 'invites') return 'Members'
  return capitaliseFirstLetter(tab)
}

const ProjectSettings: React.FC = () => {
  const { project, projectOwner } = useProject()
  const navigate = useNavigate()
  const tab = getQueryParameter('tab')

  const defaultTab = projectOwner && tab ? sectionFor(tab) : 'Information'

  const [selection, changeSelection] = React.useState<string>(defaultTab)
  const [scriptCount, setScriptCount] = useState<number>(0)

  useEffect(() => {
    document.title = `${project.name} Settings`
  }, [project.loading])

  useEffect(() => {
    // Reset so a previous hub's count can never leak into the new hub's tab list.
    setScriptCount(0)
    // ProjectProvider uses '' before the hub loads — skip the malformed /hub//scripts/count request.
    if (!projectOwner || !project.publicId) return
    let cancelled = false
    api
      .getHubScriptCount(project.publicId)
      .then((res) => {
        if (!cancelled) setScriptCount(res.data.count)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [project.publicId, projectOwner])

  useEffect(() => {
    if (tab) {
      changeSelection(sectionFor(tab))
    }
  }, [tab])
  let sections = []

  if (projectOwner && !project.isDeleted) {
    sections = ['Information', 'Styling', 'Updates', 'Archived', 'Audit', 'Members', 'Webhooks', 'Secrets']
    if (scriptCount > 0) {
      sections.push('Scripts')
    }
  } else {
    sections = ['Information']
  }
  // The section asked for, if it's one this viewer has here: a link to another (an Owner's section, for
  // someone who isn't one, or any but Information in a deactivated hub) opens on Information instead.
  const shown = sections.includes(selection) ? selection : 'Information'

  // The page header across the top, as on documents and tables ("Hub settings", with the hub before it
  // while the sidebar is hidden); below it, the sections down the left and the open one beside
  // them. When the page is narrow the sections run across the top instead. The page is a
  // container, so it's the page's own width that decides, not the window's. Each section scrolls
  // within the space left for it.
  return (
    <div className="@container flex flex-col w-full h-full">
      <PageHeader name="Hub settings" testIdPrefix="settings" />
      <div className="flex flex-1 min-h-0 w-full overflow-hidden border-t border-divider @max-4xl:flex-col">
        <SectionNav
          sections={sections}
          selectedSection={shown}
          onClick={(section) => {
            changeSelection(section)
            navigate(generatePath(HUB_SETTINGS, { projectId: project.publicId }) + `?tab=${section.toLowerCase()}`, {
              replace: true
            })
          }}
        />

        <div className="flex-1 min-w-0 min-h-0 overflow-auto">
          <ErrorBoundary>
            {shown === 'Information' && <InformationBox />}
            {projectOwner && shown === 'Styling' && <Formatting />}
            {projectOwner && shown === 'Updates' && <BulkUpdates />}
            {projectOwner && shown === 'Archived' && <DeletedResources />}
            {projectOwner && shown === 'Members' && <PermissionsBox />}
            {projectOwner && shown === 'Webhooks' && <Notifications />}
            {projectOwner && shown === 'Secrets' && <Secrets />}
            {projectOwner && shown === 'Scripts' && <ScriptsPanel />}
            {projectOwner && shown === 'Audit' && (
              <Audit type="project" resourceId={project.publicId} variant="settings" />
            )}
          </ErrorBoundary>
        </div>
      </div>
    </div>
  )
}

export default ProjectSettings
